/**
 * prop-system.ts
 *
 * Manages environmental/scenery entities ("props") — rock formations, crystal
 * clusters, energy conduits, debris fields etc. Entirely separate from
 * EnemySystem: props have no HP bars, no AI, no shooting, no points.
 *
 * Props can be:
 *   • Visual-only  — no physics body, purely decorative
 *   • Hazard       — physics body + damage on player contact
 *   • Obstacle     — physics body, no damage (blocks ball)
 */

import * as Phaser from "phaser"
import { GAME_HEIGHT } from "../data/constants"
import {
  PROP_DEFINITIONS,
  PROP_PRESETS,
  type PropSpawnPreset,
  type PropType,
} from "../data/props"
import type { GameSprite, PropEntityData } from "../types"
import type { VfxManager } from "./vfx-manager"

let _nextId = 0
function nextPropId(): string {
  return `prop_${++_nextId}`
}

export class PropSystem {
  private readonly propGroup: Phaser.Physics.Arcade.Group
  private readonly vfx: VfxManager

  constructor(
    _scene: Phaser.Scene,
    propGroup: Phaser.Physics.Arcade.Group,
    vfx: VfxManager,
  ) {
    this.propGroup = propGroup
    this.vfx = vfx
  }

  // ── Spawning ──────────────────────────────────────────────────────────────

  public spawnProp(
    propType: PropType,
    x: number,
    y: number,
    options: {
      depth?: number
      alpha?: number
      tint?: number
      scale?: number
    } = {},
  ): GameSprite | null {
    const def = PROP_DEFINITIONS[propType]
    if (!def) return null

    const sprite = this.propGroup.get(x, y, def.texture) as GameSprite | null
    if (!sprite) return null

    sprite.setActive(true).setVisible(true)
    sprite.setPosition(x, y)
    sprite.setDepth(options.depth ?? def.depth)
    sprite.setAlpha(options.alpha ?? def.alpha ?? 1)
    sprite.setDisplaySize(def.spriteSize.w, def.spriteSize.h)
    if (options.scale !== undefined) sprite.setScale(options.scale)

    const tint = options.tint ?? def.tint
    if (tint !== undefined) sprite.setTint(tint)
    else sprite.clearTint()

    const body = sprite.body as Phaser.Physics.Arcade.Body | null
    if (body) {
      body.reset(x, y)
      body.setAllowGravity(false)
      body.setImmovable(true)

      if (def.body) {
        if (def.body.shape === "circle" && def.body.radius) {
          body.setCircle(
            def.body.radius,
            (def.spriteSize.w - def.body.radius * 2) / 2 +
              (def.body.offsetX ?? 0),
            (def.spriteSize.h - def.body.radius * 2) / 2 +
              (def.body.offsetY ?? 0),
          )
        } else if (
          def.body.shape === "rect" &&
          def.body.width &&
          def.body.height
        ) {
          body.setSize(def.body.width, def.body.height)
          body.setOffset(def.body.offsetX ?? 0, def.body.offsetY ?? 0)
        }
        body.enable = true
      } else {
        // Visual-only: disable collision
        body.enable = false
      }
    }

    const entityData: PropEntityData = {
      kind: "prop",
      id: nextPropId(),
      propType,
      x,
      y,
      vx: def.scrollSpeed,
      damage: def.damage,
      isHazard: def.damage !== undefined && def.damage > 0,
    }
    sprite.entityData = entityData

    return sprite
  }

  /** Spawn a named preset (e.g. "lunar-rubble-field") starting at x. */
  public spawnPreset(presetName: string, startX: number): void {
    const groups = PROP_PRESETS[presetName]
    if (!groups) return

    let offsetX = 0
    for (const group of groups) {
      this.spawnGroup(group, startX + offsetX)
      offsetX += group.count * group.xSpread
    }
  }

  /** Spawn a scatter group of the same prop type. */
  public spawnGroup(preset: PropSpawnPreset, startX: number): void {
    const def = PROP_DEFINITIONS[preset.propType]
    const yMin = def.spawnYMin ?? 0
    const yMax = def.spawnYMax ?? GAME_HEIGHT

    for (let i = 0; i < preset.count; i++) {
      const x = startX + i * preset.xSpread + (Math.random() - 0.5) * 40
      const yCenter = (yMin + yMax) / 2
      const y = Phaser.Math.Clamp(
        yCenter + (Math.random() - 0.5) * preset.yJitter,
        yMin,
        yMax,
      )
      this.spawnProp(preset.propType, x, y)
    }
  }

  // ── Update ────────────────────────────────────────────────────────────────

  public update(_time: number, delta: number): void {
    const dt = delta / 16.6

    for (const child of this.propGroup.children) {
      const sprite = child as GameSprite
      if (!sprite.active) continue

      const ed = sprite.entityData
      if (!ed || ed.kind !== "prop") continue

      // Scroll left
      sprite.x += ed.vx * dt
      ed.x = sprite.x

      // Cull props that have scrolled off the left edge
      if (sprite.x < -300) {
        this.returnToPool(sprite)
      }
    }
  }

  // ── Collision helpers ─────────────────────────────────────────────────────

  /** Returns all active hazard props for collision setup in wave-scene. */
  public getHazardGroup(): Phaser.Physics.Arcade.Group {
    return this.propGroup
  }

  /** Resolve player touching a hazard prop — returns damage dealt (0 if not a hazard). */
  public onPlayerContact(prop: GameSprite): number {
    const ed = prop.entityData
    if (!ed || ed.kind !== "prop" || !ed.isHazard) return 0

    this.vfx.emitImpactBurst(prop.x, prop.y, 0xff4400, 0.6)
    return ed.damage ?? 0
  }

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  public clear(): void {
    for (const child of this.propGroup.children) {
      const sprite = child as GameSprite
      if (sprite.active) this.returnToPool(sprite)
    }
  }

  private returnToPool(sprite: GameSprite): void {
    sprite.setActive(false).setVisible(false)
    sprite.entityData = undefined
    const body = sprite.body as Phaser.Physics.Arcade.Body | null
    if (body) body.enable = false
  }
}
